import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import { useEffect, type ReactNode } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import NavBar from './components/NavBar';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Register from './pages/Register';
import AuthStatus from './pages/AuthStatus';
import ProtectedRoute from './components/ProtectedRoute';
// runner styles (fullscreen stage, no scroll)
import './styles/runner.css';
import { runnerTheme } from './theme';

// NEW: test launcher + existing runner pages
import TestLauncher from './pages/TestLauncher';
import Start from './pages/Start';
import Gallery from './pages/Gallery';
import Done from './pages/Done';
import ResultDetail from './pages/ResultDetail';

function Shell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const inRunner = pathname.startsWith('/runner/');
  const inAuthFlow =
    pathname === '/login' || pathname === '/register' || pathname.startsWith('/auth/');
  // Toggle "runner" class on <html> and <body> to lock the viewport & styling
  // when inside /runner/* routes, and remove when we leave.
  // This gives us true fullscreen black stage + no scroll, without touching other pages.
  // Also disables overscroll and touch zoom during the test.
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    if (inRunner) {
      root.classList.add('runner');
      body.classList.add('runner');
    } else {
      root.classList.remove('runner');
      body.classList.remove('runner');
    }
  }, [inRunner]);
  if (inRunner) {
    return <ThemeProvider theme={runnerTheme}>{children}</ThemeProvider>;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: inAuthFlow ? undefined : '100dvh',
        minHeight: inAuthFlow ? '100dvh' : undefined,
        bgcolor: inAuthFlow ? 'background.default' : undefined,
        overflow: inAuthFlow ? { xs: 'auto', md: 'hidden' } : 'hidden',
      }}
    >
      {inAuthFlow ? null : <NavBar />}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: inAuthFlow ? '100dvh' : 0,
          py: 0,
          overflow: inAuthFlow ? undefined : 'hidden',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export default function App() {
  return (
    <Shell>
      <Routes>
        {/* Default landing */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* Public */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/auth/status" element={<AuthStatus />} />

        {/* Private */}
        <Route element={<ProtectedRoute />}>
          <Route path="/dashboard" element={<Dashboard />} />
          {/* Launcher opens a separate window */}
          <Route path="/test" element={<TestLauncher />} />
          <Route path="/results/:resultId" element={<ResultDetail />} />

          {/* “Separate process” runner, no NavBar thanks to Shell */}
          <Route path="/runner/start" element={<Start />} />
          <Route path="/runner/gallery" element={<Gallery />} />
          <Route path="/runner/done" element={<Done />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Shell>
  );
}
